<template>
  <div class="updated-values-summary">
    <div v-for="(group, id) in getUpdatedFields" :key="id" class="fields">
      <h6 v-if="Object.keys(group).length">
        {{ getTabDataFromId(id) && getTabDataFromId(id).name }}
      </h6>
      <summaryCard :showAnswer="showAnswer" v-if="Object.keys(group).length">
        <div
          v-for="(
            { answer, category, field, type, questionName }, questionId
          ) in group"
          class="field"
          :key="questionId"
        >
          <div class="question">
            <p
              class="subtitle-2 caption"
              v-if="getQuestionValue({ questionId, category }).group"
            >
              {{ getQuestionValue({ questionId, category }).group }}
            </p>
            <p class="subtitle-2 question">
              {{
                questionName ||
                getQuestionValue({ questionId, category }).question
              }}
            </p>
          </div>
          <div class="answer">
            <p class="subtitle-2">
              <template v-if="showAnswer">
                <customFields
                  v-if="type"
                  :id="clientInfoRuleId"
                  :category="category"
                  :field="field"
                  readOnly
                />
                <div v-else-if="isCheckbox({ questionId, category })">
                  <checkbox :value="answer === 'Checked'" disabled />
                </div>
                <span v-else-if="isObject(answer)">
                  <nestedAnswerDisplay
                    :object="
                      convertObjectToUserFriendlyDisplay({
                        answerObject: answer,
                        category,
                      })
                    "
                  />
                </span>
                <span v-else>
                  {{ answer }}
                </span>
              </template>
            </p>
            <v-btn
              v-if="showEdit"
              icon
              @click="
                edit({
                  questionId,
                  tabId: id,
                })
              "
            >
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </summaryCard>
    </div>
  </div>
</template>
  
<script>
import { mapGetters } from "vuex";

import summaryCard from "./summary-card";

import customFields from "@/components/config-tab/custom-fields";
import tabData from "@/components/client/tabData";
import nestedAnswerDisplay from "./nested-answer-display";

import checkbox from "@/qux-common/qux22/components/molecules/checkbox";

import fieldMap from "@/components/config-field/field-map.json";

import getDisplayAnswer from "@/util/getDisplayAnswer";
import getFieldData from "@/util/getFieldData";

export default {
  components: {
    checkbox,
    customFields,
    nestedAnswerDisplay,
    summaryCard,
  },
  data() {
    return {
      tabData,
    };
  },
  props: {
    // pass in either the ID to get current updates, or pass in the updated values
    clientInfoRuleId: {
      type: String,
    },
    updatedValues: {
      type: Object,
    },
    showAnswer: {
      type: Boolean,
      default: true,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isArray(value) {
      return Array.isArray(value);
    },
    isObject(value) {
      return typeof value === "object";
    },
    isCheckbox({ questionId, category }) {
      const fieldData = this.getFieldData({ id: questionId, category });
      return fieldData && fieldData.Type === "checkbox";
    },
    getTabDataFromId(id) {
      return tabData.find((tab) => id === tab.id);
    },
    fieldList(category = this.category) {
      const fieldData = this.configTemplate && this.configTemplate[category];
      return fieldData && fieldData.AvailableFieldList;
    },
    getFieldData({ id, category }) {
      return getFieldData(this.fieldList(category), id);
    },
    getQuestionValue({ questionId, category, parentKey }) {
      let question = questionId;
      let group;
      if (questionId.includes(".")) {
        const [parent, child] = questionId.split(".");
        group = parent;
        question = child;
      }

      const fieldValue = this.getFieldValue({
        value: "label",
        category,
        id: question,
        parentKey
      });

      const groupLabel = this.getFieldValue({
        value: "label",
        category,
        id: group,
      });

      return {
        group: groupLabel !== undefined ? groupLabel : group,
        question: fieldValue !== undefined ? fieldValue : question,
      };
    },
    getDisplayAnswer({ category, questionId, answer }) {
      return getDisplayAnswer({ category, questionId, answer });
    },
    getFieldValue({ value, category, id, parentKey }) {
      let returnValue;
      if (value !== undefined && value.length > 0) {
        const fieldData = getFieldData(this.fieldList(category), id, parentKey);
        if (fieldData) {
          let configKey = value.charAt(0).toUpperCase() + value.slice(1);
          returnValue = fieldData[configKey];
        }
      }
      if (returnValue === undefined) {
        returnValue =
          fieldMap[category] &&
          fieldMap[category][id] &&
          fieldMap[category][id][value];
      }
      return returnValue;
    },
    edit({ questionId, tabId }) {
      this.$emit("edit", { questionId, tabId });
    },
    convertObjectToUserFriendlyDisplay({ answerObject, category }) {
      const processObject = (object, parentKey) => {
        let result;
        if (this.isArray(object)) {
          result = [];
        } else {
          result = {};
        }
        for (const [key, value] of Object.entries(object)) {
          if (this.isObject(value)) {
            result[key] = processObject(value, key);
          } else {
            let questionValue = this.getQuestionValue({
              questionId: isNaN(key) ? key : parentKey,
              category,
              parentKey,
            }).question;
            // Allow empty string to be a question value
            questionValue = questionValue !== undefined ? questionValue : key;
            // need special handling for places where we use 'IsEnabled'.  Don't want to show that text to user, will instead show the parent key that is actually what is enabled
            if (questionValue === "IsEnabled") {
              const fieldValue = this.getFieldValue({
                value: "label",
                category,
                id: parentKey,
              });
              questionValue = fieldValue !== undefined ? fieldValue : parentKey;
            }
            const answerValue = getDisplayAnswer({
              category,
              questionId: key,
              answer: object[key],
            });
            result[key] = {
              questionValue,
              answerValue: answerValue || object[key],
            };
          }
        }
        return result;
      };
      return processObject(answerObject);
    },
  },
  computed: {
    getUpdatedFields() {
      // Need to display the fields in the correct category groups per the client layout configs
      const layouts = this.getLayouts;

      let results = {};

      const getValue = ({ category, field, parentField, fieldId }) => {
        let value;

        const configValues = this.updatedValues || this.configValues;

        parentField
          ? (value =
              configValues &&
              configValues[(field && field.category) || category] &&
              configValues[(field && field.category) || category][
                parentField
              ] &&
              configValues[(field && field.category) || category][parentField][
                fieldId || field.id
              ])
          : (value =
              configValues &&
              configValues[(field && field.category) || category] &&
              configValues[(field && field.category) || category][
                fieldId || (field && field.id)
              ]);
        return value;
      };

      const checkValue = ({
        category,
        field,
        parentField,
        fieldId,
        results,
        id,
      }) => {
        const value = getValue({ category, field, parentField, fieldId });

        if (value !== undefined) {
          results[id][fieldId || field.id] = {
            answer: this.getDisplayAnswer({
              category: field.category || category,
              questionId: fieldId || field.id,
              answer: value,
            }),
            category: field.category || category,
          };
        }
      };

      if (layouts) {
        for (const { category, cards, id } of layouts) {
          if (!results[id]) results[id] = {};
          for (const card of cards) {
            for (const row of card.fields)
              for (const field of row) {
                if (field.id) {
                  checkValue({
                    category: card.category || category,
                    field,
                    results,
                    id,
                  });
                }
                if (field.layout) {
                  for (const layout of field.layout) {
                    for (const subfield of layout) {
                      if (subfield.items) {
                        for (const items of subfield.items) {
                          if (items.id) {
                            checkValue({
                              category: card.category || category,
                              field,
                              fieldId: items.id,
                              results,
                              id,
                            });
                          }
                        }
                      } else if (subfield.id)
                        checkValue({
                          category: card.category || category,
                          field,
                          fieldId: subfield.id,
                          results,
                          id,
                        });
                    }
                  }
                }
                if (field.items) {
                  for (const item of field.items) {
                    if (item.items) {
                      for (const subitem of item.items) {
                      checkValue({
                        category: card.category || category,
                        field: subitem,
                        parentField: item.id,
                        results,
                        id,
                      });
                    }
                    } else {
                      checkValue({
                        category: card.category || category,
                        field: item,
                        parentField: field.id,
                        results,
                        id,
                      });
                    }
                  }
                }
                if (field.fields) {
                  for (const subfield of field.items) {
                    checkValue({
                      category: card.category || category,
                      field: subfield,
                      parentField: field.id,
                      results,
                      id,
                    });
                  }
                }
                if (
                  field.type &&
                  field.type === "custom" &&
                  field.answerFields &&
                  // do any of the answer fields have a value in them
                  field.answerFields.find(
                    (answer) =>
                      getValue({
                        category: card.category || category,
                        field,
                        fieldId: answer,
                      }) !== undefined
                  )
                ) {
                  results[id][field.id] = {
                    category: field.category || category,
                    type: "custom",
                    questionName: field.questionName,
                    field: field.field,
                  };
                }
              }
          }
        }
      }
      return results;
    },
    configValues() {
      return this.updatedConfigValues[this.clientInfoRuleId];
    },
    ...mapGetters({
      configTemplate: "getConfigTemplate",
      updatedConfigValues: "getUpdatedConfigValues",
    }),
  },
  asyncComputed: {
    async getLayouts() {
      const layouts = [];
      for (const tab of tabData) {
        const layout = await import(`@/components/client/configs/${tab.id}`);
        layouts.push({ id: tab.id, ...layout });
      }
      return layouts;
    },
  },
};
</script>
  
<style lang="scss" scoped>
.fields h6 {
  padding-top: $qvo-spacing-24;
  padding-bottom: 12px;
}
.field {
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  gap: $qvo-spacing-16;
  min-height: 81px;
  border-bottom: 1px solid $qvo-color-grey-200;
  &:last-child {
    border-bottom: none;
  }
}
.question {
  padding: 20px 0 20px $qvo-spacing-16;
}
.answer {
  padding: 20px $qvo-spacing-16 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .v-icon {
    color: $qvo-color-blue-400;
  }
}
</style>